import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, finalize, Subject, takeUntil } from 'rxjs';
import { AppFilesService } from 'src/app/services/app-files.service';
import { CommonService } from 'src/app/services/common.service';
import { NotificationService } from 'src/app/services/notification.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { GeneralService } from 'src/app/platform/nomenclatoare/general/general.service';
import { Generic } from 'src/app/interfaces/generic.interface';
import { GeneralData } from '../dashboard-documente/document.interface';
import { InfoDocumentComponent } from '../dashboard-documente/info-doc/info-document.component';
import { helper } from 'src/environments/helper';
import { ActivityFiltersPayload } from '../shared/activity-filters/activity-filters.interface';
import {
    DrillDownDocumentRow,
    DrillDownPair,
    MatrixFromToResponse,
    MatrixFromToRow,
    MatrixGranularity,
    MatrixSummary,
    NedistribuiteResponse,
} from './raport-activitate-detaliat.interface';
import { RaportActivitateDetaliatService } from './raport-activitate-detaliat.service';

@Component({
    selector: 'app-raport-activitate-detaliat',
    templateUrl: './raport-activitate-detaliat.component.html',
    styleUrls: ['./raport-activitate-detaliat.component.scss'],
})
export class RaportActivitateDetaliatComponent implements OnInit, OnDestroy {
    helper = helper;

    isLoading = false;
    isGeneralDataLoading = false;

    matrixResponse: MatrixFromToResponse | null = null;
    summary: MatrixSummary | null = null;
    rows: MatrixFromToRow[] = [];
    nedistribuite: NedistribuiteResponse | null = null;
    nedistribuiteStatusMode: 'open' | 'closed' | 'all' = 'open';

    selectedRow$ = new BehaviorSubject<MatrixFromToRow | null>(null);
    selectedPair: DrillDownPair | null = null;
    tipDocumentFilter: number | null = null;
    currentFilters: ActivityFiltersPayload | null = null;
    initialPayload: Partial<ActivityFiltersPayload> | undefined;

    generalData: GeneralData[] = [];
    directii: Generic[] = [];

    private destroy$ = new Subject<void>();
    private dialogRef: MatDialogRef<InfoDocumentComponent> | null = null;
    private historyStateAdded = false;

    constructor(
        public permissionsService: PermissionsService,
        private service: RaportActivitateDetaliatService,
        private appFilesService: AppFilesService,
        private notificationService: NotificationService,
        private commonService: CommonService,
        private generalService: GeneralService,
        private dialog: MatDialog,
        private router: Router,
        private route: ActivatedRoute,
    ) { }

    ngOnInit(): void {
        this.loadGeneralData();
        this.loadDirectiiActive();
        this.hydrateFromQueryParams();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.selectedRow$.complete();
    }

    private hydrateFromQueryParams(): void {
        const params = this.route.snapshot.queryParamMap;
        if (params.keys.length === 0) return;

        const num = (k: string): number | null => {
            const v = params.get(k);
            return v !== null && v !== '' ? Number(v) : null;
        };
        const str = (k: string): string => params.get(k) ?? '';

        const payload: Partial<ActivityFiltersPayload> = {
            origin: {
                directie_id: num('origin_directie_id'),
                directie_name: str('origin_directie_name'),
                compartiment_id: num('origin_compartiment_id'),
                compartiment_name: str('origin_compartiment_name'),
                user_id: num('origin_user_id'),
                user_name: str('origin_user_name'),
            },
            destinatie: {
                directie_id: num('dest_directie_id'),
                directie_name: str('dest_directie_name'),
                compartiment_id: num('dest_compartiment_id'),
                compartiment_name: str('dest_compartiment_name'),
                user_id: num('dest_user_id'),
                user_name: str('dest_user_name'),
            },
            info: {
                number: params.get('number'),
                date_input_start: str('date_start'),
                date_input_end: str('date_end'),
                id_serie: num('id_serie'),
                registru_type: num('registru_type'),
                id_categorii: this.parseIdsParam(params.get('id_categorii')),
                source_id: num('source_id'),
                tip_document_id: num('tip_document_id'),
                activity_status: this.parseStringsParam(params.get('activity_status')),
                term_status: this.parseStringsParam(params.get('term_status')),
            },
        };
        this.initialPayload = payload;
    }

    private parseIdsParam(raw: string | null): number[] | null {
        if (!raw) return null;
        const arr = raw
            .split(',')
            .map((v) => Number(v.trim()))
            .filter((n) => Number.isFinite(n) && n > 0);
        return arr.length ? arr : null;
    }

    private parseStringsParam(raw: string | null): string[] | null {
        if (!raw) return null;
        const arr = raw
            .split(',')
            .map((v) => v.trim())
            .filter((v) => v.length > 0);
        return arr.length ? arr : null;
    }

    loadGeneralData(): void {
        this.isGeneralDataLoading = true;
        this.generalService
            .list()
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => (this.isGeneralDataLoading = false)),
            )
            .subscribe({
                next: (response: any) => {
                    this.generalData = response?.data ?? [];
                },
                error: (err) => console.error('Error loading general data', err),
            });
    }

    loadDirectiiActive(): void {
        this.commonService
            .getDirectiiActive()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (results: Generic[]) => (this.directii = results),
                error: (err) => console.error('Error loading directii', err),
            });
    }

    onSearch(payload: ActivityFiltersPayload): void {
        if (!payload.info.date_input_start || !payload.info.date_input_end) {
            this.notificationService.warningSwal(
                helper.dialogConfig.headers.errorAtentie,
                'Selectează intervalul de dată (de la / până la) înainte de a căuta.',
                helper.dialogConfig.icons.error,
            );
            return;
        }

        this.currentFilters = payload;
        this.syncQueryParams(payload);

        this.isLoading = true;
        this.selectedRow$.next(null);
        this.nedistribuite = null;
        this.loadNedistribuite(payload);

        this.service
            .getMatrix(payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (resp) => {
                    this.matrixResponse = resp;
                    this.summary = resp?.summary ?? null;
                    this.rows = resp?.rows ?? [];
                },
                error: (err) => {
                    let msg: string;
                    try {
                        msg = err.error.errors.message?.[0] ?? helper.dialogConfig.generalMessages.error;
                    } catch {
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        helper.dialogConfig.headers.errorAtentie,
                        msg,
                        helper.dialogConfig.icons.error,
                    );
                },
            });
    }

    private loadNedistribuite(payload: ActivityFiltersPayload): void {
        const withMode: ActivityFiltersPayload = {
            ...payload,
            info: { ...payload.info, nedistribuite_status_mode: this.nedistribuiteStatusMode },
        };
        this.service.getNedistribuite(withMode).subscribe({
            next: (resp) => (this.nedistribuite = resp),
            error: (err) => {
                console.error('Error loading nedistribuite', err);
                this.nedistribuite = null;
            },
        });
    }

    onNedistribuiteStatusMode(mode: string): void {
        this.nedistribuiteStatusMode = mode === 'closed' || mode === 'all' ? mode : 'open';
        if (this.currentFilters) {
            this.loadNedistribuite(this.currentFilters);
        }
    }

    refreshSearch(): void {
        if (this.currentFilters) {
            this.onSearch(this.currentFilters);
        }
    }

    onReset(): void {
        this.matrixResponse = null;
        this.summary = null;
        this.rows = [];
        this.nedistribuite = null;
        this.nedistribuiteStatusMode = 'open';
        this.currentFilters = null;
        this.selectedRow$.next(null);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            queryParamsHandling: '',
        });
    }

    onRowClick(row: MatrixFromToRow | null): void {
        this.selectedPair = row ? this.computePair(row) : null;
        this.tipDocumentFilter = null;
        this.selectedRow$.next(row);
    }

    /** Click pe chip-ul „tip document" din matrice → deschide drill-down-ul perechii, pre-filtrat pe acel tip. */
    onTipChipClick(ev: { row: MatrixFromToRow; tipDocumentId: number }): void {
        this.selectedPair = this.computePair(ev.row);
        this.tipDocumentFilter = ev.tipDocumentId;
        this.selectedRow$.next(ev.row);
    }

    private computePair(row: MatrixFromToRow): DrillDownPair {
        const oriId = this.idFromRow(row, 'origin', row.granularity_origin);
        const destId = this.idFromRow(row, 'destinatie', row.granularity_destinatie);
        return {
            origin_id: oriId,
            origin_level: row.granularity_origin,
            destination_id: destId,
            destination_level: row.granularity_destinatie,
        };
    }

    private idFromRow(row: MatrixFromToRow, side: 'origin' | 'destinatie', level: MatrixGranularity): number | null {
        if (side === 'origin') {
            if (level === 'directie') return row.origin_directie_id;
            if (level === 'compartiment') return row.origin_compartiment_id;
            return row.origin_user_id;
        }
        if (level === 'directie') return row.destinatie_directie_id;
        if (level === 'compartiment') return row.destinatie_compartiment_id;
        return row.destinatie_user_id;
    }

    openDoc(rowData: DrillDownDocumentRow): void {
        if (!this.permissionsService.hasPermission('indoc-module.rapoarte-activitati.matrix.view-document')) {
            return;
        }

        history.pushState({ modal: true }, '', window.location.href);
        this.historyStateAdded = true;

        this.dialogRef = this.dialog.open(InfoDocumentComponent, {
            minHeight: '100vh',
            maxHeight: '100vh',
            minWidth: '100vw',
            maxWidth: '100vw',
            disableClose: true,
            data: {
                docDataListDT: rowData,
                generalData: this.generalData,
                directii: this.directii,
            },
        });

        this.dialogRef.afterClosed().subscribe(() => {
            if (this.historyStateAdded) {
                this.historyStateAdded = false;
                history.replaceState(null, '', window.location.href);
            }
            this.dialogRef = null;
        });
    }

    exportFile(type: 'csv' | 'pdf'): void {
        if (!this.currentFilters) {
            this.notificationService.warningSwal(
                helper.dialogConfig.headers.errorAtentie,
                'Generează raportul înainte de a-l exporta.',
                helper.dialogConfig.icons.error,
            );
            return;
        }
        this.isLoading = true;
        const url = type === 'csv' ? this.service.exportCsvUrl : this.service.exportPdfUrl;

        const payload = {
            ...this.currentFilters,
            format: type,
        };

        this.appFilesService
            .postDownloadFile(url, payload)
            .then(() => (this.isLoading = false))
            .catch((err) => {
                console.error('Export error:', err);
                this.isLoading = false;
            });
    }

    private syncQueryParams(p: ActivityFiltersPayload): void {
        const qp: Record<string, string | number | null> = {
            origin_directie_id: p.origin.directie_id,
            origin_directie_name: p.origin.directie_name || null,
            origin_compartiment_id: p.origin.compartiment_id,
            origin_compartiment_name: p.origin.compartiment_name || null,
            origin_user_id: p.origin.user_id,
            origin_user_name: p.origin.user_name || null,
            dest_directie_id: p.destinatie.directie_id,
            dest_directie_name: p.destinatie.directie_name || null,
            dest_compartiment_id: p.destinatie.compartiment_id,
            dest_compartiment_name: p.destinatie.compartiment_name || null,
            dest_user_id: p.destinatie.user_id,
            dest_user_name: p.destinatie.user_name || null,
            number: p.info.number,
            date_start: p.info.date_input_start || null,
            date_end: p.info.date_input_end || null,
            id_serie: p.info.id_serie,
            registru_type: p.info.registru_type,
            id_categorii: Array.isArray(p.info.id_categorii) && p.info.id_categorii.length ? p.info.id_categorii.join(',') : null,
            source_id: p.info.source_id,
            tip_document_id: p.info.tip_document_id,
            activity_status: Array.isArray(p.info.activity_status) && p.info.activity_status.length ? p.info.activity_status.join(',') : null,
            term_status: Array.isArray(p.info.term_status) && p.info.term_status.length ? p.info.term_status.join(',') : null,
        };

        const cleaned: Record<string, string | number> = {};
        Object.entries(qp).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== '') {
                cleaned[k] = v;
            }
        });

        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: cleaned,
            queryParamsHandling: '',
        });
    }
}
